import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { holidayAPI } from '../../api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Calendar, Plus, Edit2, Trash2, Star, Gift } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const HolidayManagement = () => {
  const { user } = useAuth();
  const companyId = user?.company_id;
  
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    holiday_type: 'regular',
    description: ''
  });

  const loadHolidays = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await holidayAPI.getAll(companyId, selectedYear);
      setHolidays(res.data);
    } catch (error) {
      console.error('Error loading holidays:', error);
      toast.error('Failed to load holidays');
    } finally {
      setLoading(false);
    }
  }, [companyId, selectedYear]);

  useEffect(() => {
    loadHolidays();
  }, [loadHolidays]);

  const handleCreate = async () => {
    if (!formData.name || !formData.date) {
      toast.error('Please fill in required fields');
      return;
    }
    
    try {
      await holidayAPI.create(companyId, formData);
      toast.success('Holiday created');
      setCreateDialogOpen(false);
      resetForm();
      loadHolidays();
    } catch (error) {
      console.error('Error creating holiday:', error);
      toast.error('Failed to create holiday');
    }
  };

  const handleUpdate = async () => {
    if (!editingHoliday || !formData.name || !formData.date) {
      toast.error('Please fill in required fields');
      return;
    }
    
    try {
      await holidayAPI.update(editingHoliday.id, formData);
      toast.success('Holiday updated');
      setEditDialogOpen(false);
      setEditingHoliday(null);
      resetForm();
      loadHolidays();
    } catch (error) {
      console.error('Error updating holiday:', error);
      toast.error('Failed to update holiday');
    }
  };

  const handleDelete = async (holidayId) => {
    try {
      await holidayAPI.delete(holidayId);
      toast.success('Holiday deleted');
      loadHolidays();
    } catch (error) {
      console.error('Error deleting holiday:', error);
      toast.error('Failed to delete holiday');
    }
  };

  const openEditDialog = (holiday) => {
    setEditingHoliday(holiday);
    setFormData({
      name: holiday.name,
      date: holiday.date,
      holiday_type: holiday.holiday_type,
      description: holiday.description || ''
    });
    setEditDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      date: '',
      holiday_type: 'regular',
      description: ''
    });
  };

  const years = Array.from({ length: 5 }, (_, i) => (new Date().getFullYear() + i - 2).toString());

  const regularCount = holidays.filter(h => h.holiday_type === 'regular').length;
  const specialCount = holidays.filter(h => h.holiday_type === 'special_working').length;

  return (
    <div className="space-y-6" data-testid="holiday-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Holiday Management</h1>
          <p className="text-muted-foreground">Configure holidays with pay multipliers</p>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map(year => (
                <SelectItem key={year} value={year}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="add-holiday-btn">
                <Plus className="h-4 w-4" />
                Add Holiday
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Holiday</DialogTitle>
                <DialogDescription>Create a new holiday with pay multiplier</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Holiday Name *</Label>
                  <Input 
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g., New Year's Day"
                    data-testid="holiday-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Input 
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    data-testid="holiday-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Holiday Type</Label>
                  <Select 
                    value={formData.holiday_type} 
                    onValueChange={(value) => setFormData({...formData, holiday_type: value})}
                  >
                    <SelectTrigger data-testid="holiday-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="regular">
                        <div className="flex items-center gap-2">
                          <Star className="h-4 w-4 text-yellow-500" />
                          Regular Holiday (2.0x pay)
                        </div>
                      </SelectItem>
                      <SelectItem value="special_working">
                        <div className="flex items-center gap-2">
                          <Gift className="h-4 w-4 text-blue-500" />
                          Special Working Holiday (1.3x pay)
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea 
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Optional description"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setCreateDialogOpen(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button onClick={handleCreate} data-testid="confirm-add-holiday-btn">Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Calendar className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Holidays</p>
                <p className="text-2xl font-bold">{holidays.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Star className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Regular Holidays</p>
                <p className="text-2xl font-bold">{regularCount}</p>
                <p className="text-xs text-muted-foreground">2.0x pay multiplier</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Gift className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Special Working</p>
                <p className="text-2xl font-bold">{specialCount}</p>
                <p className="text-xs text-muted-foreground">1.3x pay multiplier</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Holidays Table */}
      <Card>
        <CardHeader>
          <CardTitle>Holidays for {selectedYear}</CardTitle>
          <CardDescription>Configure company holidays and pay multipliers</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : holidays.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No holidays configured for {selectedYear}</p>
              <p className="text-sm">Add holidays to apply pay multipliers</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Holiday Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Pay Multiplier</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {holidays.map((holiday) => (
                  <TableRow key={holiday.id} data-testid="holiday-row">
                    <TableCell className="font-medium">
                      {format(new Date(holiday.date + 'T00:00:00'), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell>{holiday.name}</TableCell>
                    <TableCell>
                      {holiday.holiday_type === 'regular' ? (
                        <Badge className="bg-yellow-100 text-yellow-800 gap-1">
                          <Star className="h-3 w-3" /> Regular
                        </Badge>
                      ) : (
                        <Badge className="bg-purple-100 text-purple-800 gap-1">
                          <Gift className="h-3 w-3" /> Special Working
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-bold">
                        {holiday.pay_multiplier}x
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground max-w-[200px] truncate">
                      {holiday.description || '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => openEditDialog(holiday)}
                          data-testid="edit-holiday-btn"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDelete(holiday.id)}
                          data-testid="delete-holiday-btn"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Holiday</DialogTitle>
            <DialogDescription>Update holiday details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Holiday Name *</Label>
              <Input 
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input 
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Holiday Type</Label>
              <Select 
                value={formData.holiday_type} 
                onValueChange={(value) => setFormData({...formData, holiday_type: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="regular">Regular Holiday (2.0x)</SelectItem>
                  <SelectItem value="special_working">Special Working Holiday (1.3x)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea 
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditDialogOpen(false); setEditingHoliday(null); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleUpdate}>Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HolidayManagement;
